import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { useForceStore } from "@/store/forceStore";
import { NewRosterDialog } from "./NewRosterDialog";
import { RosterCard } from "./RosterCard";

/** Maximum number of saved rosters */
const MAX_ROSTERS = 50;

export function RosterLibrary() {
	const rosters = useForceStore((s) => s.rosters);
	const openRoster = useForceStore((s) => s.openRoster);
	const deleteRoster = useForceStore((s) => s.deleteRoster);

	const [showNewDialog, setShowNewDialog] = useState(false);

	// Sort by most recently edited first
	const sortedRosters = useMemo(
		() => [...rosters].sort((a, b) => b.updatedAt - a.updatedAt),
		[rosters],
	);

	const isAtLimit = rosters.length >= MAX_ROSTERS;

	return (
		<div className="h-dvh flex flex-col bg-bg-primary text-text-primary font-base overflow-hidden">
			{/* Header area — responsive padding & sizing */}
			<div className="shrink-0 pt-6 pb-4 px-4 text-center lg:pt-12 lg:pb-8 lg:px-6">
				<h1 className="text-2xl font-bold text-accent-primary tracking-tight lg:text-3xl">
					SPECTRE OPS
				</h1>
				<p className="text-text-muted text-sm mt-1">Force Builder</p>
			</div>

			{/* Content area */}
			<div className="flex-1 overflow-y-auto px-4 pb-8 lg:px-6 lg:pb-12">
				<div className="max-w-3xl mx-auto">
					{/* Action bar */}
					<div className="flex items-center justify-between mb-4 lg:mb-6">
						<div className="text-xs text-text-muted uppercase tracking-wider">
							{rosters.length} / {MAX_ROSTERS} rosters
						</div>
						<Button
							variant="primary"
							size="md"
							onClick={() => setShowNewDialog(true)}
							disabled={isAtLimit}
							title={isAtLimit ? `Maximum of ${MAX_ROSTERS} rosters reached` : "Create a new force"}
						>
							+ New Force
						</Button>
					</div>

					{/* Roster grid or empty state */}
					{sortedRosters.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-16 lg:py-24 text-center px-4">
							<div className="text-text-muted mb-4">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="48"
									height="48"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="mx-auto opacity-40"
								>
									<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
									<polyline points="14 2 14 8 20 8" />
									<line x1="12" y1="18" x2="12" y2="12" />
									<line x1="9" y1="15" x2="15" y2="15" />
								</svg>
							</div>
							<p className="text-lg text-text-secondary font-medium mb-2">
								No force rosters yet
							</p>
							<p className="text-sm text-text-muted mb-6">
								Create your first force to get started
							</p>
							<Button variant="primary" size="lg" onClick={() => setShowNewDialog(true)}>
								+ Create Your First Force
							</Button>
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
							{sortedRosters.map((roster) => (
								<RosterCard
									key={roster.id}
									roster={roster}
									onOpen={() => openRoster(roster.id)}
									onDelete={() => deleteRoster(roster.id)}
								/>
							))}
						</div>
					)}
				</div>
			</div>

			{/* New roster dialog */}
			{showNewDialog && <NewRosterDialog onClose={() => setShowNewDialog(false)} />}
		</div>
	);
}
